import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { initializeApp } from "firebase/app";
import { getFirestore, collection, getDocs, doc, writeBatch } from "firebase/firestore";

/*
  ╔═══════════════════════════════════════════════════════════════╗
  ║  NEXUS TRADES — Protected Mentorship Platform                 ║
  ║  Features: Firebase Cloud Database, DRM, Announcements        ║
  ╚═══════════════════════════════════════════════════════════════╝
*/

// ─── FIREBASE CLOUD DATABASE SETUP ─────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyAiGg4spRz1Lr9IcyrWmW-HOcZtbZxLqzM",
  authDomain: "nexustrades.firebaseapp.com",
  projectId: "nexustrades",
  storageBucket: "nexustrades.firebasestorage.app",
  messagingSenderId: "1036754198231",
  appId: "1:1036754198231:web:fff11805103295de655a92"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const DB = {
  async get(k) {
    try {
      const snap = await getDocs(collection(db, k));
      if (snap.empty) return null;
      // Convert to array and sort by ID (which is a timestamp) to keep newest items at the top
      let items = snap.docs.map(doc => doc.data());
      items.sort((a, b) => (b.id && a.id) ? b.id.localeCompare(a.id) : 0);
      return items;
    } catch (e) {
      console.error("Firebase Read Error:", e);
      return null;
    }
  },
  async set(k, v) {
    try {
      // Fetch existing items to see if we need to delete any
      const existingSnap = await getDocs(collection(db, k));
      const existingIds = new Set(existingSnap.docs.map(d => d.id));
      const currentIds = new Set(v.map(item => item.id));

      const batch = writeBatch(db);

      // Delete items that were removed
      existingIds.forEach(id => {
        if (!currentIds.has(id)) {
          batch.delete(doc(db, k, id));
        }
      });

      // Add or update current items
      v.forEach(item => {
        if (item.id) {
          batch.set(doc(db, k, item.id), item);
        }
      });

      await batch.commit();
      return true;
    } catch (e) {
      console.error("Firebase Write Error:", e);
      return false;
    }
  },
};

const SK = { students:"nt-students", videos:"nt-videos", breakdowns:"nt-breakdowns", notes:"nt-notes", chatlogs:"nt-chatlogs", activityLog:"nt-activity-log", announcements:"nt-announcements" };
const MASTER = { username:"kev1827", password:"thestudent", name:"Kev", role:"admin", email:"admin@nexustrades.com" };

// ─── UTILS & IP FETCHER ────────────────────────────────────────────────────
let cachedIP = null;
async function getIP() {
  if (cachedIP) return cachedIP;
  try { const r = await fetch("https://api.ipify.org?format=json"); const d = await r.json(); cachedIP = d.ip; return d.ip; } catch { return "Unknown"; }
}

const isAllowedDevice = () => {
  const ua = navigator.userAgent;
  const isChrome = /Chrome/.test(ua) && /Google Inc/.test(navigator.vendor) && !/Edge|Edg|OPR|Opera/.test(ua);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  return isChrome && !isMobile;
};

// ─── CSS (Light/Dark Mode Support) ─────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
:root {
  --bg0:#08090c; --bg1:#0c0d12; --bg2:#111218; --bg3:#15161e; --bg4:#1e1f2a;
  --bdr:#22232f; --bdr2:#2a2b3a; --t1:#eae8e2; --t2:#9a978d; --t3:#5e5c54;
  --gold:#c9a84c; --gold2:#ddc06a; --gold3:#a88a32;
  --gdim:rgba(201,168,76,0.10); --gglow:rgba(201,168,76,0.20); --gbdr:rgba(201,168,76,0.15);
  --grn:#4ade80; --grn-d:rgba(74,222,128,0.10);
  --red:#f87171; --red-d:rgba(248,113,113,0.10);
  --blu:#60a5fa; --blu-d:rgba(96,165,250,0.10);
}
.light-theme {
  --bg0:#f8fafc; --bg1:#ffffff; --bg2:#f1f5f9; --bg3:#ffffff; --bg4:#e2e8f0;
  --bdr:#e2e8f0; --bdr2:#cbd5e1; --t1:#0f172a; --t2:#475569; --t3:#94a3b8;
  --gold:#b4851c; --gold2:#d9a022; --gold3:#8c6511;
  --gdim:rgba(180,133,28,0.10); --gglow:rgba(180,133,28,0.20); --gbdr:rgba(180,133,28,0.25);
  --grn:#16a34a; --grn-d:rgba(22,163,74,0.15);
  --red:#dc2626; --red-d:rgba(220,38,38,0.15);
  --blu:#2563eb; --blu-d:rgba(37,99,235,0.15);
}
html,body,#root{height:100%;font-family:'Outfit',sans-serif;background:var(--bg1);color:var(--t1);transition:background 0.3s,color 0.3s;}
::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:var(--bg2)}
::-webkit-scrollbar-thumb{background:var(--bdr2);border-radius:3px}
input,textarea,select{font-family:'Outfit',sans-serif}
@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes slideIn{from{opacity:0;transform:translateX(-12px)}to{opacity:1;transform:translateX(0)}}
`;

// ─── SHARED UI ─────────────────────────────────────────────────────────────
const inp = {width:"100%",padding:"10px 14px",background:"var(--bg0)",border:"1px solid var(--bdr)",borderRadius:9,color:"var(--t1)",fontSize:13,outline:"none",transition:"border-color 0.2s"};
const lbl = {display:"block",fontSize:10,fontWeight:600,color:"var(--t3)",marginBottom:5,letterSpacing:"0.06em",textTransform:"uppercase"};
const btnG = {padding:"10px 20px",background:"linear-gradient(135deg,var(--gold2),var(--gold3))",border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",boxShadow:"0 2px 12px var(--gglow)"};
const btnO = {padding:"8px 16px",background:"var(--bg2)",border:"1px solid var(--bdr)",borderRadius:8,color:"var(--t2)",fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:"'Outfit',sans-serif"};
const btnD = {padding:"6px 12px",background:"var(--red-d)",border:"1px solid rgba(248,113,113,0.15)",borderRadius:6,color:"var(--red)",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Outfit',sans-serif"};
const card = {background:"var(--bg3)",border:"1px solid var(--bdr)",borderRadius:12,padding:20};
const mOverlay = {position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,padding:20};
const mBox = {background:"var(--bg3)",border:"1px solid var(--bdr)",borderRadius:16,padding:28,width:"100%",maxWidth:520,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,0.5)",color:"var(--t1)"};

function NTLogo({size=40,glow=false}){
  return(<div style={{width:size,height:size,borderRadius:size*0.22,background:"linear-gradient(145deg,var(--gold2),var(--gold3),var(--gold),var(--gold2))",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Outfit'",fontSize:size*0.48,fontWeight:900,color:"#fff",flexShrink:0,boxShadow:glow?"0 0 24px var(--gglow)":"0 2px 8px rgba(0,0,0,0.3)",position:"relative",overflow:"hidden"}}><span style={{position:"relative",zIndex:1,textShadow:"0 2px 4px rgba(0,0,0,0.2)"}}>NT</span></div>);
}
function Empty({icon,title,sub,action,onAction}){
  return(<div style={{textAlign:"center",padding:"50px 20px",animation:"fadeIn 0.4s"}}><div style={{fontSize:44,marginBottom:10,opacity:0.6}}>{icon}</div><div style={{fontSize:15,fontWeight:600,color:"var(--t1)",marginBottom:5}}>{title}</div><div style={{fontSize:12,color:"var(--t3)",marginBottom:18,maxWidth:320,margin:"0 auto 18px"}}>{sub}</div>{action&&<button onClick={onAction} style={btnG}>{action}</button>}</div>);
}
function Confirm({msg,onY,onN}){
  return(<div style={mOverlay} onClick={onN}><div style={{...mBox,maxWidth:360,textAlign:"center"}} onClick={e=>e.stopPropagation()}><div style={{fontSize:28,marginBottom:10}}>⚠️</div><p style={{fontSize:13,color:"var(--t1)",marginBottom:20}}>{msg}</p><div style={{display:"flex",gap:10,justifyContent:"center"}}><button onClick={onN} style={btnO}>Cancel</button><button onClick={onY} style={btnD}>Delete</button></div></div></div>);
}

// ─── RANDOM POSITION WATERMARK ─────────────────────────────────────────────
function RandomWatermark({ text, id }) {
  const [positions, setPositions] = useState([]);
  const genPositions = useCallback(() => {
    return Array.from({ length: 3 }, () => ({
      top: 10 + Math.random() * 70, // Keep safe distance from top/bottom
      left: 10 + Math.random() * 60, // Keep safe distance from left/right edges
      rot: -20 + Math.random() * 10,
      key: Math.random()
    }));
  }, []);

  useEffect(() => {
    setPositions(genPositions());
    const interval = setInterval(() => setPositions(genPositions()), 5000 + Math.random() * 5000);
    return () => clearInterval(interval);
  }, [genPositions]);

  return (
    <>
      {positions.map((p) => (
        <div key={p.key} style={{
          position: "absolute", top: `${p.top}%`, left: `${p.left}%`, zIndex: 9999,
          color: "rgba(255,255,255,0.3)", fontSize: 16, fontWeight: 800,
          fontFamily: "'JetBrains Mono',monospace", pointerEvents: "none", userSelect: "none", whiteSpace: "nowrap",
          textShadow: "0 1px 4px rgba(0,0,0,0.9), 0 0 10px rgba(0,0,0,0.6)", transform: `rotate(${p.rot}deg)`,
          transition: "top 0.8s ease, left 0.8s ease", letterSpacing: "0.02em"
        }}>
          {text}
        </div>
      ))}
      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%) rotate(-30deg)", zIndex: 9998, pointerEvents: "none", userSelect: "none", color: "rgba(255,255,255,0.1)", fontSize: 36, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap", textShadow: "0 2px 4px rgba(0,0,0,0.6)" }}>{text}</div>
    </>
  );
}

// ─── WATERMARKED VIDEO PLAYER ──────────────────────────────────────────────
function ProtectedVideoPlayer({ src, embedUrl, embedType, userEmail, userIP, userName }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isFS, setIsFS] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  const wmText = `${userEmail || userName} • IP: ${userIP || "hidden"}`;

  useEffect(() => {
    // Basic Anti-Download: Convert Base64 data to Blob URL to hide direct source
    if (src && src.startsWith("data:video")) {
      fetch(src).then(res => res.blob()).then(blob => {
        setBlobUrl(URL.createObjectURL(blob));
      });
    } else {
      setBlobUrl(src);
    }
    return () => { if (blobUrl && blobUrl.startsWith("blob:")) URL.revokeObjectURL(blobUrl); };
  }, [src]);

  useEffect(() => {
    const handler = e => e.preventDefault(); // Disable right click
    document.addEventListener("contextmenu", handler);
    const fsHandler = () => setIsFS(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", fsHandler);
    return () => { document.removeEventListener("contextmenu", handler); document.removeEventListener("fullscreenchange", fsHandler); };
  }, []);

  // Embed parsing logic
  let finalEmbedUrl = embedUrl || "";
  const isEmbed = !src && embedUrl;
  if (isEmbed) {
    if (finalEmbedUrl.includes("youtube") || finalEmbedUrl.includes("youtu.be")) {
      if (finalEmbedUrl.includes("watch?v=")) finalEmbedUrl = `https://www.youtube.com/embed/${finalEmbedUrl.split("v=")[1]?.split("&")[0]}`;
      else if (finalEmbedUrl.includes("youtu.be/")) finalEmbedUrl = `https://www.youtube.com/embed/${finalEmbedUrl.split("youtu.be/")[1]?.split("?")[0]}`;
    }
  }

  // Device Lock Check
  if (!isAllowedDevice()) {
    return (
      <div style={{ width: "100%", aspectRatio: "16/9", background: "#000", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#fff", padding: 20, textAlign: "center" }}>
        <span style={{ fontSize: 40, marginBottom: 10 }}>🚫</span>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Device / Browser Not Supported</h3>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", maxWidth: 400 }}>To protect our content, videos can only be viewed on <b>Google Chrome</b> on a Desktop PC or Mac. Mobile devices and other browsers are blocked.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", background: "#000", overflow: "hidden", borderRadius: isFS ? 0 : "12px 12px 0 0", ...(isEmbed || blobUrl ? { aspectRatio: "16/9" } : {}), ...(isFS ? { height: "100vh" } : {}) }} onContextMenu={e => e.preventDefault()}>
      {isEmbed ? (
        <iframe src={finalEmbedUrl} style={{ width: "100%", height: "100%", border: "none", pointerEvents: "auto" }} allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" />
      ) : blobUrl ? (
        <video ref={videoRef} src={blobUrl} style={{ width: "100%", height: "100%", display: "block", objectFit: "contain" }} controls controlsList="nodownload nofullscreen noremoteplayback" disablePictureInPicture playsInline />
      ) : null}

      <RandomWatermark text={wmText} id={userEmail} />

      {isEmbed && (
        <div style={{ position: "absolute", inset: 0, zIndex: 9990, background: "transparent", cursor: "pointer" }}
          onClick={() => { const el = document.querySelector('[data-shield]'); if (el) { el.style.display = 'none'; setTimeout(() => { if (el) el.style.display = 'block'; }, 3000); } }} data-shield="true" />
      )}
    </div>
  );
}

// ─── LOGIN ─────────────────────────────────────────────────────────────────
function LoginPage({ onLogin, students }) {
  const [user, setUser] = useState(""); const [pass, setPass] = useState(""); const [error, setError] = useState("");
  const go = () => {
    if (user === MASTER.username && pass === MASTER.password) { onLogin({ ...MASTER }); return; }
    const f = (students || []).find(s => s.username === user && s.password === pass);
    if (f) onLogin({ ...f, role: "student" }); else setError("Invalid credentials");
  };
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: `radial-gradient(ellipse 70% 50% at 50% 0%,var(--gdim),transparent 60%),var(--bg0)`, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 400, padding: "0 20px", animation: "fadeIn 0.6s" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ display: "inline-block", marginBottom: 14 }}><NTLogo size={50} glow /></div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: "var(--t1)", marginBottom: 4 }}>Nexus Trades</h1>
          <p style={{ color: "var(--t3)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>Mentorship Portal</p>
        </div>
        <div style={{ ...card, borderRadius: 16, padding: "26px 22px", boxShadow: "0 24px 80px rgba(0,0,0,0.2)" }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 3 }}>Welcome back</h2>
          <p style={{ color: "var(--t3)", fontSize: 12, marginBottom: 22 }}>Sign in to your mentorship portal</p>
          <div style={{ marginBottom: 12 }}><label style={lbl}>Username</label><input type="text" value={user} onChange={e => { setUser(e.target.value); setError("") }} onKeyDown={e => e.key === "Enter" && go()} placeholder="Username" style={inp} /></div>
          <div style={{ marginBottom: 18 }}><label style={lbl}>Password</label><input type="password" value={pass} onChange={e => { setPass(e.target.value); setError("") }} onKeyDown={e => e.key === "Enter" && go()} placeholder="Password" style={inp} /></div>
          {error && <div style={{ padding: "7px 12px", background: "var(--red-d)", borderRadius: 7, color: "var(--red)", fontSize: 12, marginBottom: 12, textAlign: "center" }}>{error}</div>}
          <button onClick={go} style={{ ...btnG, width: "100%", padding: "12px 0", fontSize: 14 }}>Sign In</button>
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD ─────────────────────────────────────────────────────────────
function Dashboard({ onNav, D, isAdmin, announcements, setAnnouncements, user }) {
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [newAnnounce, setNewAnnounce] = useState("");

  const myTrades = D.breakdowns.filter(b => (b.userId || MASTER.username) === user.username);
  const w = myTrades.filter(b => b.result === "win").length;
  const l = myTrades.filter(b => b.result === "loss").length;
  const wr = myTrades.length > 0 ? Math.round((w / (w + l)) * 100) : 0;
  
  const stats = [
    { label: "Videos", value: D.videos.length, color: "var(--gold)" },
    { label: "My Trades", value: myTrades.length, color: "var(--blu)" },
    { label: "My Win Rate", value: myTrades.length > 0 ? wr + "%" : "—", color: "var(--grn)" },
  ];

  const handlePostAnnounce = async () => {
    if (!newAnnounce) return;
    const u = [{ id: Date.now().toString(), text: newAnnounce, date: new Date().toLocaleDateString() }, ...(announcements||[])];
    setAnnouncements(u); await DB.set(SK.announcements, u);
    setNewAnnounce(""); setShowAnnounce(false);
  };

  return (
    <div style={{ animation: "fadeIn 0.4s" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 26 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800 }}>Dashboard</h1>
          <p style={{ color: "var(--t2)", fontSize: 13, marginTop: 4 }}>Welcome back to Nexus Trades.</p>
        </div>
        {isAdmin && <button onClick={() => setShowAnnounce(true)} style={btnO}>+ Announcement</button>}
      </div>

      {announcements?.length > 0 && (
        <div style={{ ...card, marginBottom: 22, borderLeft: "4px solid var(--gold)", padding: "16px 20px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", marginBottom: 8, letterSpacing: "0.05em" }}>📢 Latest Announcement</div>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--t1)" }}>{announcements[0].text}</p>
          <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 8 }}>{announcements[0].date}</div>
        </div>
      )}

      {showAnnounce && (
        <div style={mOverlay} onClick={() => setShowAnnounce(false)}><div style={mBox} onClick={e => e.stopPropagation()}>
          <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 12 }}>Post Announcement</h2>
          <textarea style={{ ...inp, minHeight: 100, resize: "vertical" }} value={newAnnounce} onChange={e => setNewAnnounce(e.target.value)} placeholder="Type announcement here..." />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}><button onClick={() => setShowAnnounce(false)} style={btnO}>Cancel</button><button onClick={handlePostAnnounce} style={btnG}>Post</button></div>
        </div></div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 22, flexWrap: "wrap" }}>
        {stats.map((s, i) => (
          <div key={i} style={{ ...card, flex: 1, minWidth: 140, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: s.color, opacity: 0.5 }} />
            <div style={{ fontSize: 10, fontWeight: 600, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.color, fontFamily: "'JetBrains Mono'" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {(D.videos.some(v => v.isPinned) || D.notes.some(n => n.isPinned)) && (
        <div style={{ marginBottom: 22 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}><span style={{color:"var(--gold)"}}>★</span> Featured Content</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 12 }}>
            {D.videos.filter(v => v.isPinned).map(v => (
              <div key={v.id} onClick={() => onNav("videos")} style={{ ...card, padding: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--gbdr)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: "var(--gdim)", color: "var(--gold)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>▶</div>
                <div><div style={{ fontSize: 12, fontWeight: 600 }}>{v.title}</div><div style={{ fontSize: 10, color: "var(--t3)" }}>Video Lesson</div></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── VIDEO LIBRARY ─────────────────────────────────────────────────────────
function VideoLibrary({ videos, setVideos, isAdmin, user, actLog, setActLog, students, setStudents }) {
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [viewerIP, setViewerIP] = useState(null);
  const [newComment, setNewComment] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => { getIP().then(setViewerIP); }, []);

  const modules = ["All", ...new Set(videos.map(v => v.module || "Uncategorized"))].sort();
  const [filter, setFilter] = useState("All");

  const saveVideo = async (data) => {
    let updated;
    if (data.id) { updated = videos.map(v => v.id === data.id ? { ...v, ...data } : v); }
    else { updated = [{ ...data, id: Date.now().toString(), date: new Date().toISOString().split("T")[0], comments: [] }, ...videos]; }
    setVideos(updated); await DB.set(SK.videos, updated);
    if (selected && data.id === selected.id) setSelected({ ...selected, ...data });
    setShowAdd(false); setEditItem(null);
  };

  const deleteVideo = async (id) => {
    const updated = videos.filter(v => v.id !== id);
    setVideos(updated); await DB.set(SK.videos, updated);
    setDeleting(null); setSelected(null);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => saveVideo({ title: file.name.replace(/\.[^/.]+$/, ""), desc: "", module: "Week 1", isPinned: false, fileData: ev.target.result, embedType: "file" });
    reader.readAsDataURL(file);
  };

  const togglePin = async (e, v) => {
    e.stopPropagation();
    saveVideo({ ...v, isPinned: !v.isPinned });
  };

  const postComment = async () => {
    if (!newComment) return;
    const comment = { id: Date.now().toString(), user: user.name, text: newComment, date: new Date().toLocaleString() };
    const updatedVideo = { ...selected, comments: [...(selected.comments || []), comment] };
    saveVideo(updatedVideo);
    setNewComment("");
  };

  const markWatched = async (video) => {
    if (isAdmin) return; // Don't track admin
    if (!user.watchedVideos?.includes(video.id)) {
      const updatedUser = { ...user, watchedVideos: [...(user.watchedVideos||[]), video.id] };
      const updatedStudents = students.map(s => s.id === user.id ? updatedUser : s);
      setStudents(updatedStudents); await DB.set(SK.students, updatedStudents);
    }
    const entry = { id: Date.now().toString(), action: "video_view", videoTitle: video.title, email: user.email, ip: viewerIP || "Unknown", user: user.name, timestamp: new Date().toISOString() };
    const updatedAct = [entry, ...(actLog || [])];
    setActLog(updatedAct); await DB.set(SK.activityLog, updatedAct);
  };

  const VideoForm = ({ item, onSave, onClose }) => {
    const [f, setF] = useState(item || { title: "", desc: "", module: "Week 1", url: "", embedType: "youtube", isPinned: false });
    return (
      <div style={mOverlay} onClick={onClose}><div style={mBox} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>{item ? "Edit Video" : "Add Video"}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div><label style={lbl}>Title</label><input style={inp} value={f.title} onChange={e => setF({ ...f, title: e.target.value })} /></div>
          <div><label style={lbl}>Description</label><textarea style={{ ...inp, minHeight: 70 }} value={f.desc} onChange={e => setF({ ...f, desc: e.target.value })} /></div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}><label style={lbl}>Module / Folder</label><input style={inp} value={f.module} onChange={e => setF({ ...f, module: e.target.value })} placeholder="e.g. Week 1: Basics" /></div>
            <div style={{ flex: 1 }}><label style={lbl}>Source</label><select style={inp} value={f.embedType} onChange={e => setF({ ...f, embedType: e.target.value })}><option value="youtube">YouTube</option><option value="gdrive">Google Drive</option><option value="file">File (Blob)</option></select></div>
          </div>
          {f.embedType !== "file" && <div><label style={lbl}>Video URL</label><input style={inp} value={f.url} onChange={e => setF({ ...f, url: e.target.value })} /></div>}
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer", marginTop: 5 }}>
            <input type="checkbox" checked={f.isPinned} onChange={e => setF({ ...f, isPinned: e.target.checked })} /> Pin to Dashboard
          </label>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}><button onClick={onClose} style={btnO}>Cancel</button><button onClick={() => { if (f.title) onSave(f) }} style={btnG}>Save</button></div>
      </div></div>
    );
  };

  if (selected) {
    return (
      <div style={{ animation: "fadeIn 0.3s" }}>
        <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: "var(--gold)", fontSize: 12, cursor: "pointer", marginBottom: 14, fontWeight: 600 }}>← Back to Library</button>
        <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 20 }}>
          <ProtectedVideoPlayer src={selected.fileData} embedUrl={selected.url} embedType={selected.embedType} userEmail={user.email} userIP={viewerIP} userName={user.name} />
          <div style={{ padding: "20px" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <span style={{ padding: "3px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700, background: "var(--bg4)", color: "var(--t2)" }}>{selected.module}</span>
            </div>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{selected.title}</h1>
            <p style={{ fontSize: 13, color: "var(--t2)", lineHeight: 1.6 }}>{selected.desc}</p>
          </div>
        </div>

        <div style={card}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Q&A / Comments</h3>
          <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
            <input style={{ ...inp, flex: 1 }} value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Ask a question..." onKeyDown={e => e.key === "Enter" && postComment()} />
            <button onClick={postComment} style={btnG}>Post</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {(selected.comments || []).map(c => (
              <div key={c.id} style={{ padding: 12, background: "var(--bg2)", borderRadius: 8, border: "1px solid var(--bdr)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span style={{ fontSize: 12, fontWeight: 600, color: c.user === "Kev" ? "var(--gold)" : "var(--t1)" }}>{c.user}</span><span style={{ fontSize: 10, color: "var(--t3)" }}>{c.date}</span></div>
                <div style={{ fontSize: 13, color: "var(--t2)" }}>{c.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const filtered = videos.filter(v => filter === "All" || v.module === filter).sort((a,b) => (b.isPinned === a.isPinned ? 0 : b.isPinned ? 1 : -1));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22 }}>
        <div><h1 style={{ fontSize: 22, fontWeight: 800 }}>Video Library</h1><p style={{ color: "var(--t2)", fontSize: 13 }}>Organized course modules.</p></div>
        {isAdmin && <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => fileInputRef.current?.click()} style={btnO}>📁 Upload File</button>
          <input ref={fileInputRef} type="file" accept="video/*" onChange={handleFileUpload} style={{ display: "none" }} />
          <button onClick={() => setShowAdd(true)} style={btnG}>+ Add Video</button>
        </div>}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 5 }}>
        {modules.map(m => (
          <button key={m} onClick={() => setFilter(m)} style={{ padding: "6px 14px", borderRadius: 20, border: "1px solid", borderColor: filter === m ? "var(--gold)" : "var(--bdr)", background: filter === m ? "var(--gdim)" : "var(--bg3)", color: filter === m ? "var(--gold)" : "var(--t2)", fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>{m}</button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 12 }}>
        {filtered.map(v => {
          const isWatched = user.watchedVideos?.includes(v.id);
          return (
            <div key={v.id} style={{ ...card, padding: 0, overflow: "hidden", cursor: "pointer", position: "relative", border: v.isPinned ? "1px solid var(--gold)" : "1px solid var(--bdr)" }} onClick={() => { setSelected(v); markWatched(v); }}>
              <div style={{ height: 140, background: "var(--bg2)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "var(--gold)" }}>▶</div>
                {v.isPinned && <div style={{ position: "absolute", top: 8, right: 8, background: "var(--gold)", color: "#000", padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>PINNED</div>}
              </div>
              <div style={{ padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{v.title}</div>
                  {isAdmin && (
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={e => togglePin(e, v)} style={{ background:"none", border:"none", cursor:"pointer", color: v.isPinned ? "var(--gold)" : "var(--t3)" }}>★</button>
                      <button onClick={e => { e.stopPropagation(); setEditItem(v); }} style={{ background:"none", border:"none", cursor:"pointer", fontSize: 12 }}>✏️</button>
                      <button onClick={e => { e.stopPropagation(); setDeleting(v.id); }} style={{ background:"none", border:"none", cursor:"pointer", fontSize: 12 }}>🗑</button>
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "var(--t3)" }}>{v.module}</div>
                {!isAdmin && isWatched && <div style={{ marginTop: 8, fontSize: 10, color: "var(--grn)", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}><span>✓</span> Watched</div>}
              </div>
            </div>
          );
        })}
      </div>

      {showAdd && <VideoForm onSave={saveVideo} onClose={() => setShowAdd(false)} />}
      {editItem && <VideoForm item={editItem} onSave={saveVideo} onClose={() => setEditItem(null)} />}
      {deleting && <Confirm msg="Delete video?" onY={() => deleteVideo(deleting)} onN={() => setDeleting(null)} />}
    </div>
  );
}

// ─── TRADING JOURNALS ──────────────────────────────────────────────────────
function TradingJournals({ breakdowns, setBreakdowns, isAdmin, user, students }) {
  const [view, setView] = useState("list"); // 'list' or 'calendar'
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [viewingUser, setViewingUser] = useState(isAdmin ? null : user.username);

  const save = async (d) => { 
    const u = [{ ...d, id: Date.now().toString(), userId: viewingUser }, ...breakdowns]; 
    setBreakdowns(u); await DB.set(SK.breakdowns, u); setShowAdd(false); 
  };
  const del = async (id) => { const u = breakdowns.filter(b => b.id !== id); setBreakdowns(u); await DB.set(SK.breakdowns, u); };

  const Form = ({ onClose }) => {
    const [f, setF] = useState({ ticker: "NQ1!", direction: "Long", entry: "", exit: "", pnl: "", result: "win", notes: "", date: new Date().toISOString().split("T")[0], image: null });
    const handleImg = e => { const file = e.target.files[0]; if(file){ const r = new FileReader(); r.onload=ev=>setF({...f, image: ev.target.result}); r.readAsDataURL(file); } };
    return (<div style={mOverlay} onClick={onClose}><div style={mBox} onClick={e => e.stopPropagation()}>
      <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>Add Trade</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div><label style={lbl}>Ticker</label><input style={inp} value={f.ticker} onChange={e=>setF({...f,ticker:e.target.value})} /></div>
        <div><label style={lbl}>Result</label><select style={inp} value={f.result} onChange={e=>setF({...f,result:e.target.value})}><option value="win">Win</option><option value="loss">Loss</option></select></div>
        <div><label style={lbl}>Entry</label><input style={inp} value={f.entry} onChange={e=>setF({...f,entry:e.target.value})} /></div>
        <div><label style={lbl}>Exit</label><input style={inp} value={f.exit} onChange={e=>setF({...f,exit:e.target.value})} /></div>
      </div>
      <div style={{marginBottom:10}}><label style={lbl}>P&L</label><input style={inp} value={f.pnl} onChange={e=>setF({...f,pnl:e.target.value})} /></div>
      <div style={{marginBottom:10}}><label style={lbl}>Date</label><input type="date" style={inp} value={f.date} onChange={e=>setF({...f,date:e.target.value})} /></div>
      <div style={{marginBottom:10}}><label style={lbl}>Notes / Analysis</label><textarea style={{...inp, minHeight:60}} value={f.notes} onChange={e=>setF({...f,notes:e.target.value})} /></div>
      <div style={{marginBottom:10}}><label style={lbl}>Chart Image</label><input type="file" accept="image/*" onChange={handleImg} style={{fontSize:11, color:"var(--t2)"}} /></div>
      {f.image && <img src={f.image} style={{width:"100%", borderRadius:8, maxHeight:150, objectFit:"cover"}} alt="preview" />}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}><button onClick={onClose} style={btnO}>Cancel</button><button onClick={()=>save(f)} style={btnG}>Add Trade</button></div>
    </div></div>);
  };

  if (isAdmin && !viewingUser) {
    const allUsers = [{ ...MASTER, id: 'admin' }, ...students];
    return (
      <div style={{ animation: "fadeIn 0.3s" }}>
        <div style={{ marginBottom: 26 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800 }}>Student Journals</h1>
          <p style={{ color: "var(--t2)", fontSize: 13, marginTop: 4 }}>Review execution and analysis across all students.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 12 }}>
          {allUsers.map(u => {
            const count = breakdowns.filter(b => (b.userId || MASTER.username) === u.username).length;
            return (
              <div key={u.username} onClick={() => setViewingUser(u.username)} style={{ ...card, cursor: "pointer", display: "flex", alignItems: "center", gap: 14, transition: "border 0.2s" }} onMouseEnter={e=>e.currentTarget.style.borderColor="var(--gold)"} onMouseLeave={e=>e.currentTarget.style.borderColor="var(--bdr)"}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: "var(--bg4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "var(--t1)" }}>{u.name[0]}</div>
                <div><div style={{ fontSize: 14, fontWeight: 600 }}>{u.name}</div><div style={{ fontSize: 11, color: "var(--t3)", marginTop: 2 }}>{count} Trades Logged</div></div>
                <div style={{ marginLeft: "auto", color: "var(--t3)", fontSize: 16 }}>→</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const myBreakdowns = breakdowns.filter(b => (b.userId || MASTER.username) === viewingUser);
  const dates = [...new Set(myBreakdowns.map(b => b.date))].sort((a,b)=>b.localeCompare(a));
  const canEdit = user.username === viewingUser; 
  const canDelete = canEdit || isAdmin; 
  const viewingName = viewingUser === MASTER.username ? MASTER.name : (students.find(s => s.username === viewingUser)?.name || viewingUser);

  return (
    <div style={{ animation: "fadeIn 0.3s" }}>
      {isAdmin && (
        <button onClick={() => setViewingUser(null)} style={{ background: "none", border: "none", color: "var(--gold)", fontSize: 12, cursor: "pointer", marginBottom: 16, fontWeight: 600 }}>← Back to Directory</button>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22 }}>
        <div><h1 style={{ fontSize: 22, fontWeight: 800 }}>{isAdmin ? `${viewingName}'s Journal` : "My Trading Journal"}</h1><p style={{ color: "var(--t2)", fontSize: 13 }}>Daily execution & analysis.</p></div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ display: "flex", background: "var(--bg2)", borderRadius: 8, padding: 4 }}>
            <button onClick={()=>setView("list")} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: view==="list"?"var(--bg4)":"transparent", color: view==="list"?"var(--t1)":"var(--t3)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>List</button>
            <button onClick={()=>setView("calendar")} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: view==="calendar"?"var(--bg4)":"transparent", color: view==="calendar"?"var(--t1)":"var(--t3)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Calendar</button>
          </div>
          {canEdit && <button onClick={() => setShowAdd(true)} style={btnG}>+ Add Trade</button>}
        </div>
      </div>
      {myBreakdowns.length === 0 && <Empty icon="📊" title="No trades logged" sub={canEdit ? "Add your first trade to start tracking." : "This student hasn't logged any trades yet."} />}

      {view === "list" && myBreakdowns.length > 0 ? (
        dates.map(date => {
          const dayTrades = myBreakdowns.filter(b => b.date === date);
          return (
            <div key={date} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gold)", marginBottom: 8, fontFamily: "'JetBrains Mono'" }}>{date}</div>
              {dayTrades.map(b => (
                <div key={b.id} style={{ ...card, marginBottom: 8, padding: 0, borderLeft: `4px solid ${b.result==="win"?"var(--grn)":"var(--red)"}` }}>
                  <div onClick={()=>setExpanded(expanded===b.id?null:b.id)} style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", cursor: "pointer" }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}><span style={{ fontWeight: 700, fontFamily: "'JetBrains Mono'" }}>{b.ticker}</span><span style={{ fontSize: 11, color: "var(--t3)" }}>{b.entry} → {b.exit}</span></div>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}><span style={{ fontWeight: 700, color: b.result==="win"?"var(--grn)":"var(--red)", fontFamily: "'JetBrains Mono'" }}>{b.pnl}</span>{canDelete && <button onClick={e=>{e.stopPropagation(); del(b.id)}} style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer"}}>🗑</button>}</div>
                  </div>
                  {expanded === b.id && (
                    <div style={{ padding: "0 16px 16px", borderTop: "1px solid var(--bdr)", marginTop: 4, paddingTop: 12 }}>
                      <p style={{ fontSize: 12, color: "var(--t2)", lineHeight: 1.6, marginBottom: 10 }}>{b.notes}</p>
                      {b.image && <img src={b.image} style={{ width: "100%", maxWidth: 400, borderRadius: 8, border: "1px solid var(--bdr)" }} alt="chart" />}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        })
      ) : view === "calendar" && myBreakdowns.length > 0 ? (
        <div style={{ ...card, padding: 20 }}>
          <p style={{ fontSize: 12, color: "var(--t3)", textAlign: "center" }}>Calendar View Active. Trade dots represent wins (green) and losses (red) on specific days.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, marginTop: 20 }}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d=><div key={d} style={{textAlign:"center",fontSize:10,fontWeight:700,color:"var(--t3)"}}>{d}</div>)}
            {Array.from({length: 30}).map((_, i) => {
              const dayT = myBreakdowns.filter(b => b.date.endsWith((i+1).toString().padStart(2,"0")));
              return (
                <div key={i} style={{ aspectRatio: "1", background: "var(--bg2)", borderRadius: 8, border: "1px solid var(--bdr)", padding: 4, position: "relative" }}>
                  <span style={{ fontSize: 10, color: "var(--t3)" }}>{i+1}</span>
                  <div style={{ display: "flex", gap: 2, flexWrap: "wrap", marginTop: 4 }}>
                    {dayT.map(t => <div key={t.id} style={{ width: 8, height: 8, borderRadius: "50%", background: t.result==="win"?"var(--grn)":"var(--red)" }} />)}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}
      {showAdd && <Form onClose={()=>setShowAdd(false)} />}
    </div>
  );
}

// ─── RESOURCES ─────────────────────────────────────────────────────────────
function Resources({ notes, setNotes, isAdmin }) {
  const [showAdd, setShowAdd] = useState(false);
  const save = async (d) => { const u = [{ ...d, id: Date.now().toString(), date: new Date().toLocaleDateString() }, ...notes]; setNotes(u); await DB.set(SK.notes, u); setShowAdd(false); };
  const del = async (id) => { const u = notes.filter(n => n.id !== id); setNotes(u); await DB.set(SK.notes, u); };
  
  const Form = ({ onClose }) => {
    const [f, setF] = useState({ title: "", desc: "", isPinned: false, fileUrl: null, fileName: "" });
    const handleFile = e => { const file = e.target.files[0]; if(file){ const r = new FileReader(); r.onload=ev=>setF({...f, fileUrl: ev.target.result, fileName: file.name}); r.readAsDataURL(file); } };
    return (<div style={mOverlay} onClick={onClose}><div style={mBox} onClick={e => e.stopPropagation()}>
      <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>Add Resource</h2>
      <div style={{marginBottom:10}}><label style={lbl}>Title</label><input style={inp} value={f.title} onChange={e=>setF({...f,title:e.target.value})} /></div>
      <div style={{marginBottom:10}}><label style={lbl}>Description</label><textarea style={{...inp, minHeight:60}} value={f.desc} onChange={e=>setF({...f,desc:e.target.value})} /></div>
      <div style={{marginBottom:10}}><label style={lbl}>Upload PDF / File (or Notion Link)</label>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input type="file" onChange={handleFile} style={{fontSize:11, color:"var(--t2)"}} />
          <span style={{ fontSize: 10, color: "var(--t3)" }}>OR</span>
          <input style={{...inp, flex:1}} placeholder="https://notion.so/..." value={!f.fileName ? f.fileUrl||"" : ""} onChange={e=>setF({...f, fileUrl:e.target.value, fileName:""})} />
        </div>
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}><input type="checkbox" checked={f.isPinned} onChange={e=>setF({...f, isPinned:e.target.checked})} /> Pin Resource</label>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}><button onClick={onClose} style={btnO}>Cancel</button><button onClick={()=>save(f)} style={btnG}>Add</button></div>
    </div></div>);
  };

  const sorted = notes.sort((a,b) => (b.isPinned === a.isPinned ? 0 : b.isPinned ? 1 : -1));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22 }}>
        <div><h1 style={{ fontSize: 22, fontWeight: 800 }}>Resources</h1><p style={{ color: "var(--t2)", fontSize: 13 }}>PDFs, Notion guides, and tools.</p></div>
        {isAdmin && <button onClick={() => setShowAdd(true)} style={btnG}>+ Add Resource</button>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 12 }}>
        {sorted.map(n => (
          <div key={n.id} style={{ ...card, position: "relative", border: n.isPinned ? "1px solid var(--gold)" : "1px solid var(--bdr)" }}>
            {n.isPinned && <div style={{ position: "absolute", top: -10, left: 14, background: "var(--gold)", color: "#000", padding: "2px 8px", borderRadius: 4, fontSize: 9, fontWeight: 700 }}>PINNED</div>}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, marginTop: n.isPinned ? 4 : 0 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600 }}>{n.title}</h3>
              {isAdmin && <button onClick={()=>del(n.id)} style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer"}}>🗑</button>}
            </div>
            <p style={{ fontSize: 12, color: "var(--t2)", marginBottom: 14, lineHeight: 1.5 }}>{n.desc}</p>
            <button onClick={()=>window.open(n.fileUrl, "_blank")} style={{ ...btnO, width: "100%", background: "var(--bg2)", color: "var(--t1)", border: "1px solid var(--bdr2)" }}>
              {n.fileName ? `📄 Open ${n.fileName}` : "🔗 Open Link"}
            </button>
          </div>
        ))}
      </div>
      {showAdd && <Form onClose={()=>setShowAdd(false)} />}
    </div>
  );
}

// ─── MANAGE STUDENTS ───────────────────────────────────────────────────────
function ManageStudents({ students, setStudents, videos }) {
  const [showAdd, setShowAdd] = useState(false);
  const save = async (d) => { const u = [...students, { ...d, id: Date.now().toString(), watchedVideos: [] }]; setStudents(u); await DB.set(SK.students, u); setShowAdd(false); };
  const del = async (id) => { const u = students.filter(s => s.id !== id); setStudents(u); await DB.set(SK.students, u); };
  
  const Form = ({ onClose }) => {
    const [f, setF] = useState({ username: "", password: "", name: "", email: "" });
    return (<div style={mOverlay} onClick={onClose}><div style={mBox} onClick={e => e.stopPropagation()}>
      <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>Add Student</h2>
      <div style={{marginBottom:10}}><label style={lbl}>Name</label><input style={inp} value={f.name} onChange={e=>setF({...f,name:e.target.value})} /></div>
      <div style={{marginBottom:10}}><label style={lbl}>Email</label><input style={inp} value={f.email} onChange={e=>setF({...f,email:e.target.value})} /></div>
      <div style={{marginBottom:10}}><label style={lbl}>Username</label><input style={inp} value={f.username} onChange={e=>setF({...f,username:e.target.value})} /></div>
      <div style={{marginBottom:10}}><label style={lbl}>Password</label><input style={inp} value={f.password} onChange={e=>setF({...f,password:e.target.value})} /></div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}><button onClick={onClose} style={btnO}>Cancel</button><button onClick={()=>save(f)} style={btnG}>Add</button></div>
    </div></div>);
  };

  const totalV = videos.length;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22 }}>
        <div><h1 style={{ fontSize: 22, fontWeight: 800 }}>Manage Students</h1><p style={{ color: "var(--t2)", fontSize: 13 }}>Accounts & Progress tracking.</p></div>
        <button onClick={() => setShowAdd(true)} style={btnG}>+ Add Student</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {students.map(s => {
          const w = (s.watchedVideos || []).length;
          const pct = totalV > 0 ? Math.round((w / totalV) * 100) : 0;
          return (
            <div key={s.id} style={{ ...card, display: "flex", alignItems: "center", gap: 16, padding: "16px 20px" }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--bg4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700 }}>{s.name[0]}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{s.name} <span style={{fontSize:10, color:"var(--t3)", fontWeight:400, marginLeft:6}}>({s.username})</span></div>
                <div style={{ fontSize: 11, color: "var(--t3)" }}>{s.email}</div>
              </div>
              <div style={{ width: 150 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 4, fontWeight: 600 }}><span>Progress</span><span style={{color:"var(--gold)"}}>{w}/{totalV} ({pct}%)</span></div>
                <div style={{ height: 6, background: "var(--bg4)", borderRadius: 3, overflow: "hidden" }}><div style={{ width: `${pct}%`, height: "100%", background: "var(--gold)", borderRadius: 3 }} /></div>
              </div>
              <button onClick={()=>del(s.id)} style={btnD}>Remove</button>
            </div>
          )
        })}
      </div>
      {showAdd && <Form onClose={()=>setShowAdd(false)} />}
    </div>
  );
}

// ─── GLOBAL SEARCH COMPONENT ───────────────────────────────────────────────
function GlobalSearch({ D, onNav, close }) {
  const [q, setQ] = useState("");
  const res = useMemo(() => {
    if (!q) return [];
    const low = q.toLowerCase();
    const v = D.videos.filter(x => x.title.toLowerCase().includes(low)).map(x => ({...x, _type: 'Video', _nav: 'videos'}));
    const n = D.notes.filter(x => x.title.toLowerCase().includes(low)).map(x => ({...x, _type: 'Resource', _nav: 'notes'}));
    return [...v, ...n].slice(0, 8);
  }, [q, D]);

  return (
    <div style={mOverlay} onClick={close}>
      <div style={{ ...mBox, position: "absolute", top: "15%", padding: 0, overflow: "hidden" }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--bdr)", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{fontSize:18, opacity:0.5}}>🔍</span>
          <input autoFocus value={q} onChange={e=>setQ(e.target.value)} placeholder="Search videos, resources..." style={{ flex: 1, background: "transparent", border: "none", color: "var(--t1)", fontSize: 16, outline: "none" }} />
          <button onClick={close} style={{background:"none", border:"none", color:"var(--t3)", cursor:"pointer", fontSize:18}}>✕</button>
        </div>
        <div style={{ padding: 10 }}>
          {q && res.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "var(--t3)", fontSize: 13 }}>No results found.</div>}
          {res.map(r => (
            <div key={r.id} onClick={() => { onNav(r._nav); close(); }} style={{ padding: "12px 16px", borderRadius: 8, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "background 0.2s" }} onMouseEnter={e=>e.currentTarget.style.background="var(--bg2)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{r.title}</div>
              <div style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "var(--bg4)", color: "var(--t2)" }}>{r._type}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SIDEBAR ───────────────────────────────────────────────────────────────
function Sidebar({ active, onNav, user, onLogout, toggleSearch, theme, toggleTheme, viewAsStudent, setViewAsStudent }) {
  const isAdmin = user.role === "admin";
  const actualRole = isAdmin && !viewAsStudent ? "admin" : "student";

  const nav = [
    { id: "dashboard", label: "Dashboard", icon: "⬥" },
    { id: "videos", label: "Video Library", icon: "▶" },
    { id: "breakdowns", label: "Trading Journals", icon: "◈" }, 
    { id: "notes", label: "Resources", icon: "◇" },
  ];
  if (actualRole === "admin") {
    nav.push({ id: "students", label: "Manage Students", icon: "👤" });
    nav.push({ id: "activity", label: "Activity Log", icon: "🔍" });
  }

  return (
    <div style={{ width: 220, minHeight: "100vh", background: "var(--bg1)", borderRight: "1px solid var(--bdr)", display: "flex", flexDirection: "column", position: "fixed", left: 0, top: 0, zIndex: 200, transition: "background 0.3s" }}>
      <div style={{ padding: "20px 16px", borderBottom: "1px solid var(--bdr)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <NTLogo size={34} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--t1)" }}>Nexus Trades</div>
            <div style={{ fontSize: 9, color: "var(--t3)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Mentorship</div>
          </div>
        </div>
      </div>
      
      <div style={{ padding: "12px 12px 0" }}>
        <button onClick={toggleSearch} style={{ width: "100%", padding: "8px 12px", background: "var(--bg2)", border: "1px solid var(--bdr)", borderRadius: 8, color: "var(--t2)", fontSize: 12, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left" }}><span style={{opacity:0.6}}>🔍</span> Search...</button>
      </div>

      <nav style={{ flex: 1, padding: "16px 12px", display: "flex", flexDirection: "column", gap: 2 }}>
        {nav.map((it) => (
          <button key={it.id} onClick={() => onNav(it.id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", border: "none", borderRadius: 8, cursor: "pointer", background: active === it.id ? "var(--bg3)" : "transparent", color: active === it.id ? "var(--t1)" : "var(--t2)", fontSize: 13, fontWeight: active === it.id ? 600 : 500, fontFamily: "'Outfit'", transition: "all 0.15s", textAlign: "left" }}>
            <span style={{ fontSize: 12, color: active === it.id ? "var(--gold)" : "var(--t3)" }}>{it.icon}</span>{it.label}
          </button>
        ))}
      </nav>

      <div style={{ padding: "16px 12px", borderTop: "1px solid var(--bdr)", display: "flex", flexDirection: "column", gap: 10 }}>
        {isAdmin && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--t2)", cursor: "pointer", padding: "4px 8px", background: "var(--bg2)", borderRadius: 6 }}>
            <input type="checkbox" checked={viewAsStudent} onChange={e => setViewAsStudent(e.target.checked)} /> Preview as Student
          </label>
        )}
        <button onClick={toggleTheme} style={{ ...btnO, width: "100%", padding: "8px", fontSize: 11, border: "none", background: "var(--bg2)", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          {theme === 'dark' ? "☀️ Light Mode" : "🌙 Dark Mode"}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px", background: "var(--bg2)", borderRadius: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: "var(--bg4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "var(--t1)" }}>{user.name[0]}</div>
          <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{user.name}</div><div style={{ fontSize: 9, color: "var(--gold)" }}>{actualRole.toUpperCase()}</div></div>
          <button onClick={onLogout} style={{ background: "none", border: "none", color: "var(--t3)", cursor: "pointer", fontSize: 16 }}>⎋</button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ──────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  
  // States
  const [students, setStudents] = useState([]);
  const [videos, setVideos] = useState([]);
  const [breakdowns, setBreakdowns] = useState([]);
  const [notes, setNotes] = useState([]);
  const [actLog, setActLog] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  
  // UI States
  const [searchOpen, setSearchOpen] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [viewAsStudent, setViewAsStudent] = useState(false);

  useEffect(() => {
    const s = document.createElement("style"); s.textContent = CSS; document.head.appendChild(s);
    (async () => {
      // Fetch all collections from Firebase Cloud
      const [st, v, b, n, a, an] = await Promise.all([DB.get(SK.students), DB.get(SK.videos), DB.get(SK.breakdowns), DB.get(SK.notes), DB.get(SK.activityLog), DB.get(SK.announcements)]);
      if (st) setStudents(st); if (v) setVideos(v); if (b) setBreakdowns(b); if (n) setNotes(n); if (a) setActLog(a); if (an) setAnnouncements(an);
      setLoading(false);
    })();
    return () => document.head.removeChild(s);
  }, []);

  useEffect(() => {
    document.documentElement.className = theme === 'light' ? 'light-theme' : '';
  }, [theme]);

  if (loading) return (<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg0)" }}><NTLogo size={50} glow /></div>);

  if (!user) return <LoginPage onLogin={setUser} students={students} />;

  const isAdmin = user.role === "admin" && !viewAsStudent;
  const D = { videos, breakdowns, notes };

  const pages = {
    dashboard: <Dashboard onNav={setPage} D={D} isAdmin={isAdmin} announcements={announcements} setAnnouncements={setAnnouncements} user={user} />,
    videos: <VideoLibrary videos={videos} setVideos={setVideos} isAdmin={isAdmin} user={user} actLog={actLog} setActLog={setActLog} students={students} setStudents={setStudents} />,
    breakdowns: <TradingJournals breakdowns={breakdowns} setBreakdowns={setBreakdowns} isAdmin={isAdmin} user={user} students={students} />,
    notes: <Resources notes={notes} setNotes={setNotes} isAdmin={isAdmin} />,
    students: isAdmin ? <ManageStudents students={students} setStudents={setStudents} videos={videos} /> : null,
    activity: isAdmin ? (
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 20 }}>Activity Log</h1>
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1fr 1fr 1fr", gap: 10, padding: "12px 16px", background: "var(--bg2)", fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase" }}>
            <span>User</span><span>Action</span><span>Video</span><span>IP</span><span>Time</span>
          </div>
          {actLog.slice(0, 100).map(a => (
            <div key={a.id} style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1fr 1fr 1fr", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--bdr)", fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>{a.user}</span>
              <span style={{ color: "var(--t2)" }}>Viewed Video</span>
              <span style={{ color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.videoTitle}</span>
              <span style={{ fontFamily: "'JetBrains Mono'", fontSize: 11, color: "var(--blu)" }}>{a.ip}</span>
              <span style={{ fontSize: 10, color: "var(--t3)" }}>{new Date(a.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    ) : null,
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg0)" }}>
      <Sidebar active={page} onNav={setPage} user={user} onLogout={() => { setUser(null); setPage("dashboard"); setViewAsStudent(false); }} toggleSearch={() => setSearchOpen(true)} theme={theme} toggleTheme={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} viewAsStudent={viewAsStudent} setViewAsStudent={setViewAsStudent} />
      <main style={{ flex: 1, marginLeft: 220, padding: "40px 50px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {pages[page] || pages.dashboard}
        </div>
      </main>
      {searchOpen && <GlobalSearch D={D} onNav={setPage} close={() => setSearchOpen(false)} />}
    </div>
  );
}